/**
 * 対戦ポーカーの画像マニフェスト。**素材の置き場所と焼き方を1か所に集める**。
 *
 * 🔴 **パスの先頭にスラッシュを付けない**。`assets/...` の相対で書くと
 *   `BASE_PATH=/repo/` のサブパス配信でも正しく解決される（ポータルのカバー絵と同じ約束）。
 *
 * ⚠️ ここは `src/platform/poker/` の持ち物なので、置き場も `public/assets/poker/` に掘る。
 *   ポーカーは `games/registry.ts` の登録ゲームではない（= `game_key` を持たない）ので、
 *   `public/assets/<game_key>/` の規則には入らない。
 *
 * ## 焼き方（原画 → 配信物）
 *
 * 原画は `~/Documents/hongsoft Games/14_対戦ポーカー/`（リポジトリ外）。7点で **8.5MB** あり、
 * そのままでは載せられない。**合計 440kB** まで落としてある。
 *
 * ⚠️ 440kB は配信物の合計であって、**誰かが一度に払う額ではない**:
 *   ロゴとボタン(52kB)は画面を開いたとき、勝敗の絵は**勝敗が着いた側の1枚だけ**
 *   （大小も `<picture>` が選ぶので、実際に落ちるのは 49〜93kB のうち1枚）。
 *
 * ```bash
 * magick <原画>.png -trim +repage -resize '<最大辺>x<最大辺>>' \
 *   -strip -quality <80|86|88> -define webp:method=6 public/assets/poker/images/<名前>.webp
 * ```
 *
 * ⚠️ 勝敗の絵だけ **quality 80**。放射状のグラデーションが入っていて重く、
 *   72 まで落としても 10kB しか減らない（＝落とす意味が無い）ので 80 で止めた。
 *
 * 🔴 **焼く前に、透過が本物か確かめること**（`games/cards/config/images.ts` で1度踏んでいる）:
 *
 * ```bash
 * magick <原画>.png -format "%[pixel:p{10,8}] %[pixel:p{16,8}]" info:
 * ```
 *
 * 隣り合う画素が2値を行き来していたら**透過チェッカーが画素として焼き込まれている**ので、
 * `-trim` が余白を切れず、貼ると市松模様が出る。今回の7点は
 * いずれも `srgba(0,0,0,0)`＝**本物のアルファ**だったのでそのまま `-trim` できた。
 *
 * ⚠️ 焼いた webp は**原画の複製であって参照ではない**。原画を描き直したらここも焼き直す。
 */

/** `public/` からの相対。⚠️ 先頭スラッシュを付けない。 */
const DIR = 'assets/poker/images';

/**
 * タイトルロゴ（「PLAYER VS CPU / 対戦ポーカー」）。原画 1536×1024 2.0MB
 * → `-trim`(1483×967) → 幅480 → **37.6kB**。
 *
 * 全画面の見出しと、ダッシュボードの「メダルであそぶ」のカードの両方で使う。
 * ⚠️ カード側は**遅延読込**にしてあり、「メダルであそぶ」のタブを開くまで読まれない
 *   （ポータルの初期表示に 37.6kB を足さないため）。
 */
export const POKER_LOGO = `${DIR}/logo.webp`;

/** 表示に使う実寸（`width`/`height` 属性へ渡して、読み込み前に場所を取らせる）。 */
export const POKER_LOGO_W = 480;
export const POKER_LOGO_H = 313;

/** もどるボタン（チップに ↩）。原画 1536×1024 1.5MB → `-trim`(1059×974) → 幅160 → **7.0kB**。 */
export const POKER_BACK = `${DIR}/back.webp`;
export const POKER_BACK_W = 160;
export const POKER_BACK_H = 147;

/**
 * 配当表・ディーラーの引き方の開閉ボタン。**開く向きで絵を変える**
 * （2026-09-18 ユーザー提供・`OpenCloseH.png` / `OpenCloseV.png`）。
 *
 * 🔴 **歯車をやめた理由**: 歯車は「設定を開く」の記号なのに、ここは配当表の開閉に使っていた
 *   （2026-09-17 の実機の指摘「歯車ボタンは設定画面を開くボタンだが詳細画面の開閉に使われている」）。
 *
 * ⚠️ **配当表は、画面の広さで出る場所が変わる**——広ければ卓の**右**、狭ければ卓の**下**。
 *   ⇒ 右にあるときは左右開き(◀▶)、下にあるときは上下開き(▲▼)を出す。
 *   出し分けは `<picture>` の media（`POKER_STACKED_MEDIA`）に任せる＝**落ちるのは1枚だけ**。
 *
 * ```bash
 * magick <原画>.png -fuzz 2% -trim +repage -resize 160x160 \
 *   -strip -quality 86 -define webp:method=6 public/assets/poker/images/toggle-{h,v}.webp
 * ```
 *
 * ⚠️ **`-fuzz 2%` が要る**。原画には**ほぼ透明の靄**が外側に広がっていて、素の `-trim` だと
 *   1436×965（比 1.49:1）で切れ、丸いボタンが四角い枠の中で**3割小さく**表示されてしまう。
 *   2% で切ると 851×864 ＝ 見えている円にぴったり合う。金の縁は欠けない（拡大して確認済み）。
 *
 * ※ 歯車の `info.webp` は**消した**（ここでしか使っていなかった）。
 *   2026-09-20 に設定ボタンが要になったので、注記どおり `Button_Setting.png` から焼き直した
 *   （下の `POKER_SETTINGS`）。
 */
export const POKER_TOGGLE_H = `${DIR}/toggle-h.webp`;
export const POKER_TOGGLE_H_W = 158;
export const POKER_TOGGLE_H_H = 160;
export const POKER_TOGGLE_V = `${DIR}/toggle-v.webp`;
export const POKER_TOGGLE_V_W = 157;
export const POKER_TOGGLE_V_H = 160;

/**
 * 設定（音量・BGM）を開くボタン（2026-09-20・ユーザー提供 `Button_Setting.png`）。
 *
 * 🔴 **歯車はここでこそ正しい**。2026-09-17 に「歯車は設定を開く記号なのに配当表の開閉に
 *   使われている」と指摘されて上の ◀▶ へ差し替えた経緯があり、⚠️ **記号の意味を戻した**形。
 *
 * ```bash
 * magick Button_Setting.png -fuzz 2% -trim +repage -resize 160x160 \
 *   -strip -quality 86 -define webp:method=6 public/assets/poker/images/settings.webp
 * ```
 *
 * ⚠️ 上の開閉ボタンと**同じ手順**（`-fuzz 2%`）。原画 1536×1024 は素の `-trim` だと
 *   1457×988 で切れるが、外側はほぼ透明の靄なので 2% で 1042×904 ＝ 見えている絵に合う。
 * 💡 48px の卓のチップでは**歯車の輪郭と金の縁**しか読めないので、札とチップは切れてよい。
 */
export const POKER_SETTINGS = `${DIR}/settings.webp`;
export const POKER_SETTINGS_W = 160;
export const POKER_SETTINGS_H = 139;

/**
 * 配当表が**卓の下へ回る**画面の広さ。ここを境に開く向きが変わる。
 *
 * 🔴 **CSS の `@media` と `<picture>` の両方がこれを読む**。同じ数を2か所に書くと、
 *   片方だけ動かしたときに「下に回っているのに左右開きの絵が出る」形で黙って食い違う。
 */
export const POKER_STACKED_MEDIA = '(max-width: 860px)';

/**
 * 勝敗の絵。**大小2種類を出し分ける**（2026-09-15 ユーザー指示
 * 「勝敗画面のサイズに合わせて選択。描画位置に余裕があれば Large を優先」）。
 *
 * - **Large** = 「YOU WIN!」「YOU LOSE…」の**横長の帯**（比 約3.0:1）
 * - **Small** = 王冠と月桂樹の**正方形に近い紋章**（比 約1.1:1）
 *   ※ 2026-09-19 に「PLAYER VS CPU」の絵柄から差し替え（ユーザー提供・`Win.png` / `Lose.png`）。
 *     引き分けの `DRAW` と**同じ絵柄の組**にするため（実機チェックの回答）。
 *
 * 🔴 **既定は Large**。横長の帯は、卓の2列の札に挟まれた**横に広く縦に浅い場所**に
 *   ちょうど収まる形をしている。実測でどの画面幅でも帯の幅は 358px 以上あり、
 *   題字が読めなくなることはない。
 *
 * ⚠️ **Small へ落とすのは「縦長の狭い画面」だけ**＝スマホを立てて持ったとき。
 *   そこだけは横幅が足りず、横長の帯を置くと**上下に大きな空白が残る**ので、
 *   正方形の紋章のほうが場所に合う。iPad は縦向きでも幅 820px あるので **Large のまま**。
 *
 * 出し分けは `<picture>` の `media` に任せる＝**選ばれた1枚しか落ちない**
 * （`display:none` で隠す作りだと両方落ちる）。
 */
export const POKER_ART_SMALL_MEDIA = '(orientation: portrait) and (max-width: 520px)';

/** 勝ち。原画 1450×478 / 770×820 → `-trim` → 幅960 / 620 → **58kB / 93kB**。 */
export const POKER_WIN_LARGE = `${DIR}/win-large.webp`;
export const POKER_WIN_LARGE_W = 960;
export const POKER_WIN_LARGE_H = 318;
export const POKER_WIN_SMALL = `${DIR}/win-small.webp`;
export const POKER_WIN_SMALL_W = 714;
export const POKER_WIN_SMALL_H = 620;

/** 負け。原画 1450×540 / 800×820 → `-trim` → 幅960 / 620 → **49kB / 72kB**。 */
export const POKER_LOSE_LARGE = `${DIR}/lose-large.webp`;
export const POKER_LOSE_LARGE_W = 960;
export const POKER_LOSE_LARGE_H = 296;
export const POKER_LOSE_SMALL = `${DIR}/lose-small.webp`;
export const POKER_LOSE_SMALL_W = 634;
export const POKER_LOSE_SMALL_H = 620;

/**
 * 引き分け。**大小を分けない1枚**（2026-09-19・実機チェックの回答「同点時に Draw 表示」）。
 *
 * 🔴 **勝ち負けと違って帯の版が無い**ので、`<picture>` で出し分けず1枚を両方に使う。
 *   比 1.55:1 は横長の帯(3.0:1)と正方形の紋章(1.1:1)の中間で、⚠️ どちらの場所でも
 *   高さで頭を押さえられる（`.pk-art` の `max-height: var(--pk-art-h)`）＝はみ出さない。
 *
 * ⚠️ **原画は「透明の市松模様」が実ピクセルとして焼き込まれていた**（`-trim` が効かず、
 *   卓に置くと市松がそのまま出る）。四隅から塗りつぶしで抜いてある:
 *
 * ```bash
 * magick Draw.png -alpha set -channel RGBA -fuzz 12% -fill none \
 *   -draw 'alpha 0,0 floodfill'       -draw 'alpha 1199,0 floodfill' \
 *   -draw 'alpha 0,760 floodfill'     -draw 'alpha 1199,760 floodfill' \
 *   +channel -trim +repage -resize 640x -strip -quality 86 -define webp:method=6 draw.webp
 * ```
 *
 * 🔴 **`-fuzz 12%` が要る**。市松は #E5E5E5 と #FEFEFE の2色（差は約10%）で、
 *   これを跨がないと片方の升目だけが残る。⚠️ **全体置換にはできない**
 *   ——札の白（#FFFFFF）まで抜けるため、四隅からの塗りつぶしで届く範囲に限る。
 */
export const POKER_DRAW = `${DIR}/draw.webp`;
export const POKER_DRAW_W = 640;
export const POKER_DRAW_H = 413;

// ───────────────────────────────────────────────────────────────────────────
// 札（2026-09-17・実機チェックの回答「トランプ札は『トランプSolitaire』のカードに変更」）
// ───────────────────────────────────────────────────────────────────────────

/**
 * 52枚シート。**「トランプSolitaire」と同じ絵**を使う（ユーザー指示）。
 *
 * 🔴 **原画は `public/assets/cards/images/cards.png`（リポジトリ内）**。他の7点と違って
 *   焼き元が手元にあるので、**焼き直しはいつでも再現できる**:
 *
 * ```bash
 * magick public/assets/cards/images/cards.png \
 *   -strip -quality 80 -define webp:method=6 public/assets/poker/images/cards.webp
 * ```
 *
 * ⚠️ **`cards.png` を直接参照しない**。理由は2つ:
 *   1. 依存の向き（`platform` は `games/<key>/` の持ち物を名指ししない）。
 *      パスの文字列でも、カード側が素材を動かした瞬間に**黙って 404 する**結合になる。
 *   2. 重さ。原画は **588kB の PNG**。webp q80 にして **276kB**。
 *
 * 🔴 **等倍（セル 220×375）で焼く**（2026-10-03・実機チェック第7回「札がゲームのメインなので
 *   出来るだけ大きなサイズで描画」）。iPad（2倍DPI）で札の高さ **187px までくっきり**出る。
 *   ⚠️ それまでの 0.6倍（セル 132×225・147.6kB）は「札の高さ 96px」の頃の寸法で、
 *   **112px を超えると引き伸ばし**になる——札を大きくした卓（iPad Pro 11 横で 160〜180px）では
 *   ぼやける。⇒ 札を大きくするなら、絵の解像度も一緒に上げる。
 *
 * ⚠️ **可逆や減色は逆に太る**（実測 可逆 709kB / 128色可逆 240kB / 64色可逆 209kB）。
 *   絵札の陰影が効いていて平坦色ではないため。⇒ 非可逆 q80。
 *   品質は**実表示の寸法まで落としてから**比べて決めた（q80 RMSE 1.22% / q86 1.12%＝
 *   30kB 払って 0.1% の差しかない）。既存の「もどる」ボタンが RMSE 1.30% で通っている。
 */
export const POKER_DECK = `${DIR}/cards.webp`;
export const POKER_DECK_W = 2860;
export const POKER_DECK_H = 1500;

/**
 * シートの格子。**列＝ランク（A,2..10,J,Q,K）／行＝スート（♠♥♦♣）**。
 * 🔴 この並びは原画（`games/cards` の `scripts/cards-sheet.mjs`）が決めている。
 */
export const POKER_DECK_COLS = 13;
export const POKER_DECK_ROWS = 4;

/** 1枚ぶんの升目。**シートの実寸から割り出す**（比を2か所に書かない）。 */
export const POKER_CARD_CELL_W = POKER_DECK_W / POKER_DECK_COLS;
export const POKER_CARD_CELL_H = POKER_DECK_H / POKER_DECK_ROWS;

/**
 * 裏面。`backs.png`（4色）の**赤**＝casino の卓の色。
 * 緑のフェルトに置くので、緑の裏面は埋もれる。青は「トランプSolitaire」のやさしい段の色。
 *
 * ```bash
 * magick public/assets/cards/images/backs.png -crop 220x375+220+0 +repage \
 *   -strip -quality 80 -define webp:method=6 public/assets/poker/images/cardback.webp
 * ```
 *
 * 🔴 表と同じく**等倍**（2026-10-03）。表だけ上げると、伏せ札だけがぼやけて並ぶ。
 *
 * ⚠️ 名前を `back.webp` にしない——そこは**もどるボタン**が使っている。
 */
export const POKER_CARD_BACK = `${DIR}/cardback.webp`;
export const POKER_CARD_BACK_W = 220;
export const POKER_CARD_BACK_H = 375;

/**
 * 角の丸みを**幅と高さの割合**で表したもの（`border-radius: <x>% / <y>%`）。
 *
 * 🔴 **px で書かない**。素材の角は 220×375 の升目で半径 **約17px** の弧
 *   （上辺 y=0 で不透明になるのが x=16、y=16 付近で x=0 に到達。実測）。
 *   札の大きさは画面で変わるので、px 固定だと小さい札で角が丸すぎ、大きい札で角ばる。
 *   割合なら**どの寸法でも素材の弧と重なる** ⇒ 影と選択中の枠が札の形に沿う。
 */
export const POKER_CARD_RADIUS_X = '7.7%';
export const POKER_CARD_RADIUS_Y = '4.5%';

/**
 * サーバーの札番号（`c >> 2` = ランク 0:'2'..12:'A' ／ `c & 3` = スート 0:♣ 1:♦ 2:♥ 3:♠）を
 * **シートの升目**へ移す。🔴 **対応表は要らない**——どちらも規則的な並びなので式1本で足りる。
 *
 * - 列: シートは A,2,3..10,J,Q,K の順。サーバーは '2' が 0 なので **A(12) だけ先頭へ回す**。
 * - 行: シートは ♠♥♦♣ の順、サーバーは ♣♦♥♠ の順＝**まるごと逆**なので `3 - suit`。
 *
 * ⚠️ 式が合っていることは `__tests__/pokerDeck.test.ts` が**手で書いた対応表**で押さえている
 *   （同じ式から期待値を作ると、何を壊しても落ちない見張りになる）。
 */
export function deckCell(card: number): { col: number; row: number } {
  const rank = card >> 2;
  const suit = card & 3;
  return { col: rank <= 11 ? rank + 1 : 0, row: 3 - suit };
}
